import { FC, PropsWithChildren, useRef, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { ApiResponse } from 'apisauce';

import {
  authenticate,
  getAtsToken,
  getDeviceList,
  getHuaweiLogin,
  getLoadBalancer,
  getProfiles,
  getReplaceTimes,
  modifyDeviceName,
  postAltiboxAuthenticate,
  postAuthenticatorFromAltibox,
  postSwitchProfile,
  replaceDevice,
} from '@api/auth/subscriberAuth';
import { atsSubscriberSessionStorageKey, useSessionStorage } from '@hooks/useSessionStorage';
import useSnack from '@hooks/useSnack';
import {
  AltiboxProfile,
  DecodedAtsToken,
  Device,
  SubscriberWithToken,
} from '@modules/simulations/types';

import SubscriberContext, { SubscriberValues } from './context';

enum RetCode {
  NO_FREE_EST_SLOT = '85983787',
  NO_FREE_NON_EST_SLOT = '85983785',
}

const SubscriberProvider: FC<PropsWithChildren> = ({ children }) => {
  const { addSnack } = useSnack();

  const [subscriber, setSubscriber] = useSessionStorage<SubscriberWithToken>(
    atsSubscriberSessionStorageKey
  );

  const [deviceList, setDeviceList] = useState<Device[]>();
  const [fullName, setFullName] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [profile, setProfile] = useState<AltiboxProfile>();
  const [profileOptions, setProfileOptions] = useState<AltiboxProfile[]>();
  const [remainingSwaps, setRemainingSwaps] = useState<number>();

  /**
   * Reference to login `Promise` resolve fn. Can be accessed and called from outside the initial `Promise`.
   */
  const resolveRef = useRef<((value: unknown) => void) | null>(null);

  /**
   * Reference to login `Promise` reject fn. Can be accessed and called from outside the initial `Promise`.
   */
  const rejectRef = useRef<((reason?: unknown) => void) | null>(null);

  const reset = () => {
    setProfileOptions(undefined);
    setProfile(undefined);
    setDeviceList(undefined);
    setFullName('');
    setIsAuthenticating(false);
    setRemainingSwaps(undefined);
    resolveRef.current = null;
    rejectRef.current = null;
  };

  const handleResolveLogin = () => {
    resolveRef.current?.(0);
    reset();
  };

  const handleRejectLogin = (err?: unknown) => {
    addSnack(String(err), { variant: 'error' });
    console.error(err);
    rejectRef.current?.(err);
    reset();
  };

  async function performRequest<T>(
    request: () => Promise<ApiResponse<T>>,
    handleError?: (response: ApiResponse<T>) => void
  ) {
    return request()
      .then(response => {
        const { originalError, ok, data } = response;
        if (originalError) throw originalError;
        if (!ok || !data) {
          handleError?.(response);
          throw new Error();
        }
        return data;
      })
      .catch(() => false as unknown as T);
  }

  const handleAltiboxAuthenticate = async (
    tmpProfile?: AltiboxProfile,
    tmpFullName?: string,
    proceedIfSuccess?: boolean
  ) => {
    const { subscriberId = '', authToken = '' } = tmpProfile || profile || {};
    try {
      const altiboxAuthenticate = await performRequest(() =>
        postAltiboxAuthenticate(authToken, subscriberId)
      );

      if (!altiboxAuthenticate) throw new Error('Unable to authenticate');

      if (
        altiboxAuthenticate.retmsg.includes(RetCode.NO_FREE_EST_SLOT) ||
        altiboxAuthenticate.retmsg.includes(RetCode.NO_FREE_NON_EST_SLOT)
      ) {
        const getDeviceListResponse = await performRequest(() => getDeviceList(subscriberId));
        if (!getDeviceListResponse?.deviceList) throw new Error('No devices');
        const getReplaceTimesResponse = await performRequest(() => getReplaceTimes(subscriberId));
        if (getReplaceTimesResponse.remainTimes)
          setRemainingSwaps(Number(getReplaceTimesResponse.remainTimes));
        if (getDeviceListResponse.deviceList.length > 0)
          setDeviceList(getDeviceListResponse.deviceList);
      } else if (altiboxAuthenticate.retcode && altiboxAuthenticate.retcode !== '0')
        throw new Error(altiboxAuthenticate.retmsg);
      // eslint-disable-next-line no-use-before-define
      else if (proceedIfSuccess) handleSelectDevice(undefined, tmpProfile, tmpFullName);
    } catch (err) {
      handleRejectLogin(err);
    }
  };

  /**
   * Login pt. 3
   */
  const handleSelectDevice = async (
    selectedDeviceId?: string,
    tmpProfile?: AltiboxProfile,
    tmpFullName?: string
  ) => {
    try {
      const { subscriberId = '', profilePin = '' } = tmpProfile || profile || {};

      if (selectedDeviceId) {
        const device = deviceList?.find(d => d.deviceId === selectedDeviceId);
        if (!device) throw new Error('No device');
        await performRequest(() => replaceDevice(subscriberId, selectedDeviceId));
        await performRequest(() => modifyDeviceName(selectedDeviceId));
        await handleAltiboxAuthenticate(tmpProfile);
      }

      setDeviceList(undefined);
      await performRequest(() => postSwitchProfile(profilePin, subscriberId));
      const atsToken = await performRequest(() => getAtsToken(subscriberId));
      if (!atsToken) throw new Error('No token');

      const { vodPlatform } = decodeToken<DecodedAtsToken>(atsToken) || {};

      getProfiles(atsToken).then(({ data }) => {
        if (data && data.length > 0) {
          setSubscriber({
            atsToken,
            name: tmpFullName ?? fullName,
            profileId: data[0].profileId,
            subscriberId,
            vodPlatform,
          });
        } else throw new Error('Could not fetch profiles');
      });

      handleResolveLogin();
    } catch (err) {
      handleRejectLogin(err);
    }
  };

  /**
   * Login pt. 2
   * @param selectedSubscriber `subscriberId` or profile object
   * @param tmpFullName Full name of the selected user
   */
  const handleSelectProfile = async (
    selectedSubscriber?: AltiboxProfile | string,
    tmpFullName?: string
  ) => {
    try {
      const tmpProfile =
        typeof selectedSubscriber === 'string'
          ? profileOptions?.find(p => p.subscriberId === selectedSubscriber)
          : selectedSubscriber;
      if (!tmpProfile) throw new Error('No profiles');
      setProfile(tmpProfile);
      setProfileOptions(undefined);
      await performRequest(() => getHuaweiLogin(tmpProfile.subscriberId));
      await handleAltiboxAuthenticate(tmpProfile, tmpFullName, true);
    } catch (err) {
      handleRejectLogin(err);
    }
  };

  /**
   * Login pt. 1
   */
  const handleLogin = async (username: string, password: string) =>
    new Promise((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;

      const performLogin = async () => {
        setIsAuthenticating(true);

        try {
          await getLoadBalancer();
          const huaweiLoginResponse = await performRequest(getHuaweiLogin);
          const authenticateResponse = await performRequest(() => authenticate(username, password));
          if (authenticateResponse.status === 'failure') throw Error(authenticateResponse.message);
          const tmpFullName = `${authenticateResponse.data.user.firstName} ${authenticateResponse.data.user.lastName}`;
          setFullName(tmpFullName);
          const { value } = await performRequest(() =>
            postAuthenticatorFromAltibox(
              huaweiLoginResponse.enctytoken,
              authenticateResponse.data.sessionTicket.identifier
            )
          );
          if (value.length > 1) setProfileOptions(value);
          else handleSelectProfile(value.at(0), tmpFullName);
        } catch (err) {
          handleRejectLogin(err);
        }
      };

      performLogin();
    });

  const handleLogout = () => setSubscriber(undefined as unknown as SubscriberWithToken);

  const values: SubscriberValues = {
    cancelLogin: reset,
    deviceList,
    handleLogin,
    handleLogout,
    handleSelectDevice,
    handleSelectProfile,
    isAuthenticating,
    profileOptions: profileOptions,
    remainingSwaps,
    subscriber,
  };

  return <SubscriberContext.Provider value={values}>{children}</SubscriberContext.Provider>;
};

export default SubscriberProvider;
